import {Component, computed, effect, input, output, signal} from '@angular/core';
import {GenericTileListSkeletonComponent} from '@app/tiles/generic-tile-list-skeleton/generic-tile-list-skeleton.component';
import {GenericTileListComponent} from '@app/tiles/generic-tile-list/generic-tile-list.component';
import {TileInfo} from '@models/tile-info';
import {InfiniteScrollDirective, InfiniteScrollSentinelDirective} from '@shared/directives/infinite-scroll.directive';

export type SearchResultsState = 'initial' | 'loading' | 'results' | 'empty';

@Component({
  selector: 'app-search-results',
  templateUrl: './search-results.component.html',
  imports: [
    InfiniteScrollDirective,
    GenericTileListComponent,
    InfiniteScrollSentinelDirective,
    GenericTileListSkeletonComponent,
  ],
})
export class SearchResultsComponent {
  readonly tiles = input<TileInfo[] | null | undefined>(undefined);
  readonly loading = input(false);
  readonly infiniteScroll = output<InfiniteScrollDirective>();

  // Don't immediately show skeleton tiles; it should only be for longer-awaited requests.
  readonly showSkeleton = signal(false);
  readonly LOADING_DELAY_MS = 200;

  constructor() {
    effect(onCleanup => {
      if (!this.loading()) {
        this.showSkeleton.set(false);
        return;
      }

      const timer = setTimeout(() => {
        if (this.loading()) {
          this.showSkeleton.set(true);
        }
      }, this.LOADING_DELAY_MS);

      onCleanup(() => clearTimeout(timer));
    });
  }

  readonly state = computed<SearchResultsState>(() => {
    const tiles = this.tiles();

    // Search has finished, with results
    if (tiles?.length) {
      return 'results';
    }

    // Search is in progress (or, search is still initializing):
    // Once LOADING_DELAY_MS time has elapsed, show the skeleton tiles.
    if (tiles === undefined || this.loading()) {
      return this.showSkeleton() ? 'loading' : 'initial';
    }

    // Search has finished, with no results
    return 'empty';
  });

  onInfiniteScroll(scroll: InfiniteScrollDirective) {
    this.infiniteScroll.emit(scroll);
  }
}
